import { useEffect, useRef, useState, type ReactElement } from 'react';
import { toast } from '../../../../design-system/components';
import { trackFailed } from '../../../../../../shared-web/track';
import { useSession } from '../../../auth';
import { apiErrorCode, apiFailureReason, emberApi, isAuthRequired, type FriendsPage as FriendsData, type GroupsList } from '../../api';
import { refreshChats, useChatsLive, useChatsSnapshot } from '../../chats-store';
import { BottomSheet } from '../../components/BottomSheet';
import { EmberDisc } from '../../components/EmberDisc';
import { HOW_ONE, HOW_ROOMS, HowMark, HowSheet, howGroups, type How } from '../../components/HowItWorks';
import { Link } from '../../components/Link';
import { PageFailure } from '../../components/PageFailure';
import { PageLoading } from '../../components/PageLoading';
import { PresenceAvatar } from '../../components/PresenceAvatar';
import { plural } from '../../format';
import { navigate } from '../../router';
import { Binoculars, CHEVRON, DashedPerson, Face, FLAME, FriendsBar, NotAvailable, Sparkle, dayLabel, firstName } from './friend-parts';
import { MeetPeopleIntro } from './MeetPeopleIntro';

type Sheet = { kind: 'friend' } | { kind: 'topic'; roomId: string };
/** The three ways as their ? hands them over (HowItWorks): the name on the sheet, that way's rules, its log state. */
const ONE: How = { way: 'One person', lines: HOW_ONE, state: 'one' };
const ROOMS: How = { way: 'Topic rooms', lines: HOW_ROOMS, state: 'rooms' };
const GROUPS: How = { way: 'Groups', lines: howGroups(), state: 'groups' };

const chatOf = (roomId: string): string => `/talk?mode=chat&room=${encodeURIComponent(roomId)}`;
/** A topic's square, in turn: its ground and its ink. */
const TOPIC_TINTS: Array<[string, string]> = [['#E3ECE0', '#24603B'], ['#FBE1CF', '#8A3A07'], ['#ECE6F6', '#5B4A8A']];

/**
 * Meet people — `/friends` (2026-10-26 founder, named so 2026-10-27; mocks Friends / FriendsAll). One page for both ways of meeting people:
 * one new friend at a time (looking, an introduction out, or none), the rooms by topic they hold (rooms they sit in,
 * topics still waiting for people, and adding one while they have space), and what came before (friends they met,
 * requests they stopped). Someone who may not take part gets `{ eligible: false }` and sees nothing of it.
 *
 * Groups (2026-11-03 founder; mock Main): someone with a group open to them (GET /api/groups `open`) first sees the three
 * ways — One person, Topic rooms, Groups — each an icon, a line and who it suits (2026-11-04); One person (`?view=one`) and Topic rooms
 * (`?view=rooms`) open this page's own part for it, unchanged; Groups opens /groups. With no group open to them the page
 * is as it was.
 */
export function FriendsPage({ view = null }: { view?: string | null } = {}): ReactElement {
  const { session } = useSession();
  const [page, setPage] = useState<FriendsData | null>(null);
  const [failed, setFailed] = useState(false);
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [busy, setBusy] = useState(false);
  const [intro, setIntro] = useState(false);
  const [how, setHow] = useState<How | null>(null);
  const [groups, setGroups] = useState<GroupsList | null>(null);
  const help = useRef<HTMLButtonElement>(null);

  // Reads overlap (the first, the live channel's, coming back to the tab): only the newest one's answer is the page.
  const reads = useRef(0);
  // The first read marks the page opened (friends.pageFor): the home's sign stops saying "new!" — read its list again.
  const been = useRef(false);
  const load = (): void => {
    const seq = ++reads.current;
    emberApi.friends()
      .then((r) => {
        if (r.eligible && !been.current) { been.current = true; void refreshChats(); }
        if (seq !== reads.current) return;
        setPage(r);
        setFailed(false);
      })
      .catch((err: unknown) => {
        if (seq !== reads.current) return;
        if (isAuthRequired(err)) { navigate(`/auth?returnTo=${encodeURIComponent('/friends')}`, { replace: true }); return; }
        trackFailed('friends_load', apiFailureReason(err));
        setFailed(true);
      });
    // Whether a group is open to them (the three ways in). Groups off (503 groups_unavailable) or the read failing leaves
    // the page as it was; a page already showing the ways keeps them.
    emberApi.groups()
      .then((g) => setGroups(g))
      .catch((err: unknown) => {
        if (apiErrorCode(err) !== 'groups_unavailable' && !isAuthRequired(err)) trackFailed('group_hub', apiFailureReason(err));
        setGroups((cur) => cur ?? { open: false });
      });
  };
  useEffect(() => {
    load();
    // An introduction lands, a room opens: the page reads again when the person comes back to it.
    const again = (): void => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', again);
    return () => document.removeEventListener('visibilitychange', again);
  }, []);
  // And while it stays on screen, every change the live channel announces reads it again (2026-10-27 walk-through: a topic
  // stopped on the phone stood on the desktop's page, 3 of 3, until a reload).
  useChatsLive(true);
  const chats = useChatsSnapshot().data;
  useEffect(() => { if (chats) load(); }, [chats]);

  const stop = async (target: Sheet): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      if (target.kind === 'friend') await emberApi.stopFriendRequest();
      else await emberApi.leaveRoom(target.roomId, false);
      setSheet(null);
      load();
    } catch (err: unknown) {
      trackFailed(target.kind === 'friend' ? 'friends_stop_looking' : 'friends_topic_stop', apiFailureReason(err));
      // Already stopped elsewhere (another device): the page reads again and there is nothing to try again.
      const gone = ['no_request', 'not_a_member', 'not_found'].includes(apiErrorCode(err) ?? '');
      setSheet(null);
      load();
      if (!gone) toast.error('Couldn’t stop that just now. Try again?');
    } finally {
      setBusy(false);
    }
  };

  if (!page && failed) return <PageFailure title="Couldn’t open this page" logState="load_failed" retry />;
  if (!page || !groups) return <PageLoading />;
  if (!page.eligible) return <NotAvailable />;

  const friend = page.friend ?? null;
  const rooms = page.rooms ?? { held: null, cap: null, up: [], sitting: [] };
  const met = page.earlier?.met ?? [];
  const stopped = page.earlier?.stopped ?? [];
  const firstVisit = !friend && !rooms.up.length && !rooms.sitting.length && !met.length && !stopped.length;
  // A greeting, not a count (2026-10-27 founder: "4 rooms so far!" read as all the rooms there are).
  const name = firstName(session?.self_name);
  // Which part is on screen: everything (no group open to them: the page as it was), the three ways, or one way's part.
  const part: 'all' | 'ways' | 'one' | 'rooms' = !groups.open ? 'all' : view === 'one' || view === 'rooms' ? view : 'ways';

  return (
    <main className="nf-page" data-log-seen={part === 'ways' ? 'group_hub' : 'friends_page'} data-log-state={part === 'ways' ? undefined : firstVisit ? 'first_visit' : (friend?.status || 'none')}>
      <FriendsBar to={part === 'one' || part === 'rooms' ? '/friends' : '/'} />
      <div className={`nf-head${part === 'ways' ? ' is-ways' : ''}`}>
        {part === 'one' || part === 'rooms'
          ? (
            <div className="nf-title-row">
              <h1 className="nf-title">{part === 'one' ? 'One person' : 'Topic rooms'}</h1>
              <HowMark how={part === 'one' ? ONE : ROOMS} onHow={setHow} />
            </div>
          )
          : (
            <>
              {part === 'all' && <span className="nf-kicker">hi {name || 'there'}!</span>}
              {/* What this is is told by the intro behind the ?, not in words on the page (2026-10-27 founder). */}
              <div className="nf-title-row">
                <h1 className="nf-title">Meet people</h1>
                <button ref={help} type="button" className="nf-help" aria-label="What is Meet people?" data-log="friends_help" data-log-state="page" onClick={() => setIntro(true)}>
                  <span aria-hidden>?</span>
                </button>
              </div>
              {part === 'ways' && <p className="nf-ways-lede">Three ways to connect. Pick the one that sounds like you right now. You can try more than one.</p>}
            </>
          )}
      </div>
      {part === 'ways' && groups.open && <Ways groups={groups} onHow={setHow} />}
      {part === 'one' && (firstVisit
        ? <FirstVisit rooms={false} />
        : (
          <div className="nf-main">
            <FriendSlot page={page} onStop={() => setSheet({ kind: 'friend' })} />
            <Earlier friend={friend} met={met} stopped={stopped} />
          </div>
        ))}
      {part === 'rooms' && (
        <div className="nf-main">
          <div className="nf-label">
            <span>YOUR ROOMS</span>
            {rooms.held !== null && rooms.cap !== null && <span className="nf-label-aside">{rooms.held} of {rooms.cap} places</span>}
          </div>
          <Rooms rooms={rooms} onStop={(roomId) => setSheet({ kind: 'topic', roomId })} />
        </div>
      )}
      {part === 'all' && (firstVisit
        ? <FirstVisit />
        : (
          <div className="nf-main">
            <div className="nf-label">
              <span className="nf-label-name">ONE PERSON<HowMark how={ONE} onHow={setHow} small /></span>
              <span className="nf-label-aside">one at a time</span>
            </div>
            <FriendSlot page={page} onStop={() => setSheet({ kind: 'friend' })} />

            <div className="nf-label is-spaced">
              <span className="nf-label-name">YOUR ROOMS<HowMark how={ROOMS} onHow={setHow} small /></span>
              {rooms.held !== null && rooms.cap !== null && <span className="nf-label-aside">{rooms.held} of {rooms.cap} places</span>}
            </div>
            <Rooms rooms={rooms} onStop={(roomId) => setSheet({ kind: 'topic', roomId })} />

            <Earlier friend={friend} met={met} stopped={stopped} />
          </div>
        ))}

      {sheet && (
        <BottomSheet
          title={sheet.kind === 'friend' ? 'Stop looking?' : 'Stop this topic?'}
          description="I’ll stop looking. Nobody hears about it. You can start again anytime."
          logSeen={sheet.kind === 'friend' ? 'friends_stop_looking_sheet' : 'friends_topic_stop_sheet'}
          onClose={() => setSheet(null)}
        >
          <button
            type="button"
            className="ember-btn nf-btn-dark"
            disabled={busy}
            data-log={sheet.kind === 'friend' ? 'friends_stop_looking' : 'friends_topic_stop'}
            data-log-state="confirm"
            data-log-match-id={sheet.kind === 'topic' ? sheet.roomId : undefined}
            onClick={() => { void stop(sheet); }}
          >
            Stop looking
          </button>
          <button
            type="button"
            className="ember-btn ember-btn-ghost"
            data-log={sheet.kind === 'friend' ? 'friends_stop_looking' : 'friends_topic_stop'}
            data-log-state="keep"
            onClick={() => setSheet(null)}
          >
            Keep looking
          </button>
        </BottomSheet>
      )}
      {how ? <HowSheet how={how} onClose={() => setHow(null)} /> : null}
      {intro ? <MeetPeopleIntro from="page" onClose={() => setIntro(false)} foldTo={() => help.current} /> : null}
    </main>
  );
}

/** What came before: friends they met (their chat, still theirs to open) and requests they stopped (to start again). */
function Earlier({ friend, met, stopped }: { friend: FriendsData['friend'] | null; met: NonNullable<FriendsData['earlier']>['met']; stopped: NonNullable<FriendsData['earlier']>['stopped'] }): ReactElement | null {
  if (!met.length && !stopped.length) return null;
  return (
    <>
      <div className="nf-label is-spaced"><span>EARLIER</span></div>
      <section className="nf-list is-soft">
        {met.map((m) => (
          <div className="nf-row is-tight" key={m.request_id}>
            {m.them ? <Face avatar={m.them.avatar} size={44} /> : <span className="nf-row-icon is-round"><DashedPerson size={22} color="#8A8170" /></span>}
            <span className="nf-row-text">
              <span className="nf-row-name">{m.them?.name || 'Someone'}</span>
              <span className="nf-row-sub">Met {dayLabel(m.at)}</span>
            </span>
            {/* A chat that wrapped up stays theirs to read (the check-in says so): its room's page, read-only. */}
            {m.room_id && m.room_status && (
              <Link to={m.room_status === 'open' ? chatOf(m.room_id) : `/rooms/${encodeURIComponent(m.room_id)}`} className="nf-row-go" data-log="friends_earlier_chat" data-log-state={m.room_status} data-log-match-id={m.room_id}>Chat</Link>
            )}
          </div>
        ))}
        {stopped.map((s) => (
          <div className="nf-row is-tight" key={s.request_id}>
            <span className="nf-row-text">
              <span className="nf-row-what">{s.card.topics?.length ? `Into ${s.card.topics.join(' · ')}` : 'Someone'}</span>
              <span className="nf-row-when">You stopped {dayLabel(s.at)}</span>
            </span>
            {/* While a request stands there is no other to start (one at a time): its card is edited instead. */}
            {!friend && <Link to={`/friends/new?from=${encodeURIComponent(s.request_id)}`} className="nf-pill" data-log="friends_start_again" data-log-match-id={s.request_id}>Start again</Link>}
          </div>
        ))}
      </section>
    </>
  );
}

/**
 * A first visit (Friends): nothing under way and nothing earlier — what this is, and the two ways in. On One person's own
 * part (the three ways, Groups 2026-11-03) the way to rooms is left out: rooms have their own part.
 */
function FirstVisit({ rooms = true }: { rooms?: boolean } = {}): ReactElement {
  return (
    <div className="nf-main is-roomy">
      <section className="nf-hero">
        <div className="nf-hero-band">
          <svg className="nf-hero-art" viewBox="0 0 358 150" preserveAspectRatio="none" aria-hidden>
            <circle cx="40" cy="130" r="80" fill="#F6EAD0" />
            <circle cx="330" cy="14" r="64" fill="#E3ECE0" />
            <path d="M96 84c46-44 136-44 176 0" stroke="#E2A23C" strokeWidth="2" fill="none" strokeLinecap="round" strokeDasharray="1 7" />
          </svg>
          <span className="nf-hero-me nf-bob"><EmberDisc className="nf-hero-disc" /></span>
          <span className="nf-hero-them nf-bob is-late"><DashedPerson size={36} color="#8A8170" /></span>
          <span className="nf-hero-note">who could it be?</span>
          <Sparkle size={14} className="nf-twinkle nf-hero-spark1" />
          <Sparkle size={10} color="#F4720C" className="nf-twinkle is-late nf-hero-spark2" />
        </div>
        <div className="nf-hero-copy">
          <h2 className="nf-h2">Meet someone fun to chat with</h2>
          <p>One person, picked by me. Everyday stuff, at your own pace.</p>
        </div>
        <div className="nf-hero-go">
          <Link to="/friends/new" className="nf-cta" data-log="friends_start" data-log-state="first_visit">Let’s go</Link>
          <span className="nf-hand is-quiet">3 quick taps</span>
        </div>
      </section>

      <section className="nf-tiles">
        <div className="nf-tile is-tilt-l">
          <span className="nf-tile-icon" style={{ background: '#FBE1CF' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden><circle cx="8" cy="9" r="3" stroke="#8A3A07" strokeWidth="1.8" fill="none" /><circle cx="16" cy="9" r="3" stroke="#8A3A07" strokeWidth="1.8" fill="none" /><path d="M3 19c.8-2.8 2.8-4.3 5-4.3s4.2 1.5 5 4.3M11 19c.8-2.8 2.8-4.3 5-4.3s4.2 1.5 5 4.3" stroke="#8A3A07" strokeWidth="1.8" fill="none" strokeLinecap="round" /></svg>
          </span>
          <span>Just you two, and me</span>
        </div>
        <div className="nf-tile is-tilt-r">
          <span className="nf-tile-icon" style={{ background: '#FFF1C7' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden><circle cx="12" cy="12" r="8" stroke="#8A5A12" strokeWidth="1.8" fill="none" /><path d="M12 7.5V12l3 2" stroke="#8A5A12" strokeWidth="1.8" fill="none" strokeLinecap="round" /></svg>
          </span>
          <span>Reply whenever</span>
        </div>
        <div className="nf-tile is-tilt-s">
          <span className="nf-tile-icon" style={{ background: '#E3ECE0' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden><path d="M7 12.5l3.2 3.2L17 9" stroke="#24603B" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="12" r="8" stroke="#24603B" strokeWidth="1.8" fill="none" /></svg>
          </span>
          <span>Stop anytime</span>
        </div>
      </section>

      {rooms && <Link to="/friends/rooms/new" className="nf-roomlink" data-log="friends_topic_add" data-log-state="first_visit">
        <span className="nf-roomlink-pic" aria-hidden>
          <span className="nf-roomlink-dot is-a" />
          <span className="nf-roomlink-dot is-b" />
          <span className="nf-roomlink-flame">{FLAME}</span>
        </span>
        <span className="nf-roomlink-text">
          <span className="nf-roomlink-title">Or a room on one topic</span>
          {/* What a room is, not the cap (2026-10-26 walk-through: "Up to 3 at a time" read as three people) */}
          <span className="nf-roomlink-sub">3 to 8 people, plus me</span>
        </span>
        <span className="nf-roomlink-go">{CHEVRON}</span>
      </Link>}
    </div>
  );
}

/**
 * The three ways to meet people (Groups 2026-11-03; redesign 2026-11-04, mock Main): One person, Topic rooms, Groups —
 * each a card with its icon, what it is in a line, who it suits ("Good if you …") and a ? that says how that one works
 * (2026-11-04 founder; HowItWorks). Groups says what is new there for them (posts by others they have not opened), or
 * that it is new while they have joined none. Under them, the way to ask Ember which fits.
 */
function Ways({ groups, onHow }: { groups: Extract<GroupsList, { open: true }>; onHow: (how: How) => void }): ReactElement {
  const joined = groups.groups.filter((g) => g.joined);
  const fresh = joined.reduce((n, g) => n + g.fresh, 0);
  const badge = fresh > 0 ? `${fresh} new` : joined.length === 0 ? 'NEW' : '';
  return (
    <div className="nf-main nf-ways">
      <Way to="/friends?view=one" state="one" tint={['#FBE7D8', '#B94B0A']} title="One person"
        sub="Just you and one person Ember picks for you. Write back and forth, at your own pace."
        good={['want someone new to talk to', 'like one-on-one more than groups', 'are open to where it goes']}
        how={ONE} onHow={onHow}
        icon={<><circle cx="9" cy="10" r="3.5" /><circle cx="19" cy="10" r="3.5" /><path d="M3.5 22c.8-3.6 3-5.5 5.5-5.5s4.7 1.9 5.5 5.5" /><path d="M13.5 22c.8-3.6 3-5.5 5.5-5.5s4.7 1.9 5.5 5.5" /></>} />
      <Way to="/friends?view=rooms" state="rooms" tint={['#E3ECE0', '#24603B']} title="Topic rooms"
        sub="A small chat about one topic. 3 to 8 people plus Ember, talking together."
        good={['have something you want to talk about', 'enjoy chatting with a few people', 'like a lively back-and-forth']}
        how={ROOMS} onHow={onHow}
        icon={<><path d="M4 6.5h14a2 2 0 0 1 2 2V15a2 2 0 0 1-2 2H10l-4 3.5V17H4a2 2 0 0 1-2-2V8.5a2 2 0 0 1 2-2z" /><path d="M20 11h2.5a2 2 0 0 1 2 2v5.5a2 2 0 0 1-2 2H22V24l-3.5-3.5H14" /><circle cx="7" cy="12" r="0.6" fill="currentColor" /><circle cx="11" cy="12" r="0.6" fill="currentColor" /><circle cx="15" cy="12" r="0.6" fill="currentColor" /></>} />
      <Way to="/groups" state="groups" tint={['#ECE6F6', '#5B4A8A']} title="Groups" badge={badge}
        sub="People living through the same thing. Post a question or what’s going on; others answer when they can."
        good={['are going through something hard', 'want to hear from people who’ve been there', 'would rather post than chat live']}
        how={GROUPS} onHow={onHow}
        icon={<><rect x="4" y="4" width="20" height="9" rx="2.5" /><path d="M8 8.5h9" /><rect x="7" y="16" width="17" height="8" rx="2.5" /><path d="M11 20h9" /><path d="M4 16v5.5" /></>} />
      <p className="nf-ways-ask">Not sure? <Link to="/talk?mode=chat" data-log="group_hub_ask">Ask Ember which one fits</Link>.</p>
    </div>
  );
}

/**
 * One way in: its icon on its tint, its name (and badge), the ? that opens its rules, its line, the chevron; then
 * "Good if you" and three badges. The whole card is the link — the name carries it over the card (nf-way-title::after),
 * so the ? is a button of its own rather than one nested inside a link.
 */
function Way({ to, state, tint, title, badge = '', sub, good, how, onHow, icon }: {
  to: string;
  state: string;
  tint: [string, string];
  title: string;
  badge?: string;
  sub: string;
  good: string[];
  how: How;
  onHow: (how: How) => void;
  icon: ReactElement;
}): ReactElement {
  return (
    <div className="nf-way">
      <span className="nf-way-row">
        <span className="nf-way-icon" style={{ background: tint[0], color: tint[1] }}>
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>{icon}</svg>
        </span>
        <span className="nf-way-text">
          <span className="nf-way-name">
            <Link to={to} className="nf-way-title" data-log="group_hub_open" data-log-state={state}>{title}</Link>
            {badge && <span className="nf-way-badge">{badge}</span>}
            <HowMark how={how} onHow={onHow} />
          </span>
          <span className="nf-way-sub">{sub}</span>
        </span>
        <span className="nf-way-chev">{CHEVRON}</span>
      </span>
      <span className="nf-way-good">
        <span className="nf-way-good-label">Good if you</span>
        <span className="nf-way-pills">{good.map((g) => <span key={g} className="nf-way-pill">{g}</span>)}</span>
      </span>
    </div>
  );
}

/** The one new friend: looking (since, topics, Edit, Stop looking), an introduction out, or none. */
function FriendSlot({ page, onStop }: { page: FriendsData; onStop: () => void }): ReactElement {
  const friend = page.friend ?? null;
  if (friend?.status === 'looking') {
    const topics = friend.card.topics || [];
    return (
      <section className="nf-card nf-looking">
        <div className="nf-looking-top">
          <span className="nf-looking-pic">
            <Face avatar={friend.card.avatar} size={56} className="nf-bob is-ring-peach" />
            <Binoculars width={34} className="nf-looking-bino" />
          </span>
          <span className="nf-looking-text">
            <span className="nf-hand">sniffing around...</span>
            <span className="nf-row-sub">Since {dayLabel(friend.since)}{topics.length ? ` · ${topics.join(', ')}` : ''}</span>
            <span className="nf-row-sub">It can take a while. I’ll let you know the moment I find someone.</span>
          </span>
        </div>
        <div className="nf-two">
          <Link to="/friends/new?edit=1" className="nf-soft-btn" data-log="friends_edit">Edit</Link>
          <button type="button" className="nf-soft-btn is-plain" data-log="friends_stop_looking" data-log-state="ask" onClick={onStop}>Stop looking</button>
        </div>
      </section>
    );
  }
  if (friend?.status === 'found') {
    const roomId = friend.intro?.room_id ?? friend.room_id;
    const them = friend.intro?.them ?? null;
    const waiting = friend.intro?.me.answer === 'yes';
    const body = (
      <>
        {them ? <Face avatar={them.avatar} size={50} className="is-ring-white" /> : <span className="nf-row-icon is-round is-white"><DashedPerson size={26} color="#B94B0A" /></span>}
        <span className="nf-row-text">
          <span className="nf-row-name is-big">{them?.name || 'Someone new'}</span>
          <span className="nf-row-sub is-ink">{waiting ? `Waiting for ${them?.name || 'them'}` : 'Ember found someone you’d get along with!'}</span>
        </span>
        <span className="nf-row-chev">{CHEVRON}</span>
      </>
    );
    return roomId
      ? <Link to={`/friends/intro/${encodeURIComponent(roomId)}`} className="nf-found" data-log="friends_intro_open" data-log-state={waiting ? 'waiting' : 'found'} data-log-match-id={roomId}>{body}</Link>
      : <div className="nf-found">{body}</div>;
  }
  // "Another" only after a friend was met; a request stopped before anyone was found met no one.
  const before = (page.earlier?.met.length ?? 0) > 0;
  return (
    <Link to="/friends/new" className="nf-again" data-log="friends_start" data-log-state="again">
      <span className="nf-row-icon is-round is-white is-big"><DashedPerson size={26} color="#B94B0A" /></span>
      <span className="nf-row-text">
        <span className="nf-again-title">{before ? 'Meet someone else?' : 'Meet someone new?'}</span>
        <span className="nf-hand is-small is-dark">whenever you feel like it</span>
      </span>
      <span className="nf-row-chev is-dark">{CHEVRON}</span>
    </Link>
  );
}

/** Rooms they sit in, their topics still waiting for people, and adding one while they have space. */
function Rooms({ rooms, onStop }: { rooms: NonNullable<FriendsData['rooms']>; onStop: (roomId: string) => void }): ReactElement {
  const canAdd = rooms.cap === null || rooms.held === null || rooms.held < rooms.cap;
  return (
    <>
      <section className="nf-list">
        {rooms.sitting.map((r) => (
          <Link to={chatOf(r.room_id)} className="nf-row" key={r.room_id} data-log="friends_room_open" data-log-match-id={r.room_id}>
            <span className="nf-huddle" aria-hidden>
              {r.people.slice(0, 2).map((p, i) => (
                <span className={`nf-huddle-face is-${i ? 'b' : 'a'}`} key={`${p.name}-${i}`}><PresenceAvatar avatarKey={p.avatar} size={22} /></span>
              ))}
              <span className="nf-huddle-flame">{FLAME}</span>
            </span>
            <span className="nf-row-text">
              <span className="nf-row-name">{r.name || r.topic}</span>
              {/* Who is in it with you (2026-10-27 founder: "3 of you" read as a room not yet joined) */}
              <span className="nf-row-sub">{r.people.length ? `You and ${plural(r.people.length, 'other')}` : 'Just you'}{r.unread > 0 ? ' · new messages' : ''}</span>
            </span>
            <span className="nf-row-go">Open</span>
          </Link>
        ))}
        {rooms.up.map((t, i) => {
          const [ground, ink] = TOPIC_TINTS[i % TOPIC_TINTS.length];
          return (
            <div className="nf-row" key={t.room_id}>
              <span className="nf-row-icon" style={{ background: ground }}><DashedPerson size={22} color={ink} /></span>
              <span className="nf-row-text">
                <span className="nf-row-name">{t.topic || t.name}</span>
                {/* As the apps say it (2026-10-26 walk-through: "1 of 3 in" sat beside the section's "1 of 3" places) */}
                <span className={`nf-row-sub${t.present > 1 ? ' is-hot' : ''}`}>{t.present > 1 ? `${t.present - 1} ${t.present === 2 ? 'other wants' : 'others want'} this too!` : `Looking since ${dayLabel(t.since)}`}</span>
              </span>
              <button type="button" className="nf-pill is-small" data-log="friends_topic_stop" data-log-state="ask" data-log-match-id={t.room_id} onClick={() => onStop(t.room_id)}>Stop</button>
            </div>
          );
        })}
        {canAdd && (
          <Link to="/friends/rooms/new" className="nf-row is-add" data-log="friends_topic_add" data-log-state="rooms">
            <span className="nf-add-box" aria-hidden>
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
            </span>
            <span className="nf-row-name">Add a topic</span>
          </Link>
        )}
      </section>
      <p className="nf-note">{canAdd ? 'A room is 3 to 8 people who picked the same thing, plus me.' : `That’s ${rooms.cap}! Leave a room or stop a topic to add another.`}</p>
    </>
  );
}
